"use client";

import { StickyNote, X, Send, Clock, AlertCircle, CheckCircle, MessageSquare, Calendar, Shield, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useCreateLeadNote, useGetLeadNotes, useRevealLeadNote } from "@/app/(authenticated)/leads/_modules/hooks/queries";

interface QuickNotesPanelProps {
  leadId: string;
  leadName?: string;
  onClose: () => void;
}

const NOTE_CATEGORIES = [
  { value: "general", label: "General", icon: StickyNote },
  { value: "follow_up", label: "Follow-up", icon: Clock },
  { value: "important", label: "Important", icon: AlertCircle },
  { value: "quote", label: "Quote", icon: CheckCircle },
  { value: "call", label: "Call", icon: MessageSquare },
  { value: "meeting", label: "Meeting", icon: Calendar },
] as const;

export const QuickNotesPanel = function ({ leadId, leadName, onClose }: QuickNotesPanelProps) {
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [category, setCategory] = useState<string>("call");

  // API hooks
  const { mutateAsync: createNoteMutation, isPending: isCreating } = useCreateLeadNote();
  const { data: notesResponse, isLoading: notesLoading } = useGetLeadNotes(leadId);
  const { mutateAsync: revealNote } = useRevealLeadNote();
  const [revealedNotes, setRevealedNotes] = useState<Record<string, string>>({});
  const [revealingNoteId, setRevealingNoteId] = useState<string | null>(null);

  // Extract recent notes from API response
  const recentNotes = (() => {
    if (!notesResponse) return [];
    const notes = Array.isArray(notesResponse) ? notesResponse : (notesResponse as any)?.data || [];
    // Sort by date and take last 3
    return notes
      .sort((a: any, b: any) => new Date(b.createdAt || b.created_at).getTime() - new Date(a.createdAt || a.created_at).getTime())
      .slice(0, 3);
  })();

  const handleSubmit = async () => {
    if (!note.trim()) return;

    try {
      await createNoteMutation({
        leadId,
        params: {
          note: note.trim(),
          category: category as "general" | "follow_up" | "important" | "quote" | "call" | "meeting",
          priority: "medium",
        },
      });

      setNote("");
      toast({
        title: "Note Saved",
        description: "Your note has been added to the lead.",
      });
    } catch (error) {
      console.error("Failed to create note:", error);
      toast({
        title: "Failed to Save Note",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleToggleReveal = async (noteId: string) => {
    if (revealedNotes[noteId]) {
      setRevealedNotes((prev) => {
        const next = { ...prev };
        delete next[noteId];
        return next;
      });
      return;
    }
    setRevealingNoteId(noteId);
    try {
      const data = await revealNote({ leadId, noteId });
      const text = typeof data === "string" ? data : (data as any)?.data || (data as any)?.note || "";
      setRevealedNotes((prev) => ({ ...prev, [noteId]: text }));
    } catch {
      toast({ title: "Failed to reveal note", variant: "destructive" });
    } finally {
      setRevealingNoteId(null);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-gray-700">Quick Notes</span>
          {leadName && (
            <span className="text-xs text-gray-500 truncate max-w-[100px]">
              - {leadName}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3">
        {/* Category selector + Note input */}
        <div className="space-y-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NOTE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-3 w-3" />
                      {cat.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your note... (Cmd+Enter to save)"
            className="min-h-[80px] text-sm resize-none"
            autoFocus
          />

          <Button
            onClick={handleSubmit}
            disabled={!note.trim() || isCreating}
            size="sm"
            className="w-full h-8 bg-amber-600 hover:bg-amber-700"
          >
            <Send className="h-3 w-3 mr-1.5" />
            {isCreating ? "Saving..." : "Add Note"}
          </Button>
        </div>

        {/* Recent notes preview */}
        {recentNotes.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">
              Recent Notes
            </div>
            <div className="space-y-1.5 max-h-[100px] overflow-y-auto">
              {recentNotes.map((n: any) => (
                <div
                  key={n.id}
                  className="text-xs p-1.5 bg-gray-50 rounded border border-gray-100"
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="text-gray-700 line-clamp-2 flex-1">{revealedNotes[n.id] || n.note}</div>
                    {n.hasSensitiveData && (
                      <button
                        className="flex-shrink-0 text-amber-500 hover:text-amber-700 mt-0.5"
                        onClick={() => handleToggleReveal(n.id)}
                        disabled={revealingNoteId === n.id}
                      >
                        {revealingNoteId === n.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : revealedNotes[n.id] ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {n.hasSensitiveData && (
                      <span className="inline-flex items-center text-[9px] px-1 py-0 bg-amber-100 text-amber-700 rounded">
                        <Shield className="h-2 w-2 mr-0.5" />
                        Sensitive
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {formatDate(n.createdAt || n.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {notesLoading && (
          <div className="text-xs text-gray-400 text-center py-2">
            Loading notes...
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickNotesPanel;
